import React from 'react';
import MuiLink from '@mui/material/Link';
import { makeStyles } from 'tss-react/mui';
import { CommonPropsAndTypes } from '../_Types/StandardComponentProps';
import EDTooltip from '../Tooltip/EDTooltip';

interface IProps {
  onClick?: React.MouseEventHandler;
  variant?: CommonPropsAndTypes.VariantType;
  inheritStyle?: boolean;
  href?: string;
  buttonComponent?: boolean;
  className?: string;
  target?: string;
  disabled?: boolean;
  hint?: string;
  disabledReason?: React.ReactNode;
  children?: React.ReactNode;
  testId?: string;
}

const useStyles = makeStyles<IProps>()((theme, props) => ({
  root: {
    color: props.inheritStyle ? 'inherit' : theme.palette.primary.light,
    fontWeight: props.inheritStyle ? 'inherit' : 'bold',
    // cursor: props.onClick ? 'pointer' : 'default',
    lineHeight: 1.2,
    marginBottom:
      props.variant === 'overline' ? theme.spacing(1.25) : theme.spacing(0),
    '&:hover': props.inheritStyle
      ? {}
      : {
          borderBottom: `${theme.spacing(0.25)} solid ${
            theme.palette.primary.light
          }`,
          marginBottom:
            props.variant === 'overline'
              ? theme.spacing(1)
              : theme.spacing(-0.25),
        },
    '&[disabled]': {
      color: theme.palette.action.disabled,
      cursor: 'default',
      '&:hover': {
        textDecoration: 'none',
        borderBottom: 'none',
        marginBottom: props.variant === 'overline' ? 10 : 0,
      },
    },
  },
  tooltipWrapper: {
    display: props.buttonComponent ? 'flex' : 'inline-flex',
    maxWidth: '100%',
    minWidth: 0,
  },
}));

const EDLink: React.FC<
  React.PropsWithChildren<
    IProps & React.RefAttributes<HTMLSpanElement & HTMLButtonElement>
  >
> = React.forwardRef<HTMLSpanElement & HTMLButtonElement, IProps>(
  (props, ref) => {
    const { classes, cx } = useStyles(props);
    const {
      buttonComponent,
      variant,
      onClick,
      disabled,
      inheritStyle,
      className,
      children,
      disabledReason,
      testId,
      ...rest
    } = props;
    const component = (
      <MuiLink
        component={buttonComponent ? 'button' : 'a'}
        variant={variant ?? 'body1'}
        onClick={onClick}
        underline={!disabled && inheritStyle && onClick ? 'hover' : 'none'}
        color={inheritStyle ? 'inherit' : undefined}
        className={cx(className, classes.root)}
        disabled={disabled}
        type={buttonComponent ? 'button' : undefined}
        ref={ref}
        data-testid={testId}
        {...rest}
      >
        {children}
      </MuiLink>
    );
    if (props.hint != null || (disabled && disabledReason)) {
      const title = disabled ? disabledReason : props.hint;
      return (
        <EDTooltip title={title} enableClickOnTouch={disabled}>
          <span className={classes.tooltipWrapper}>{component}</span>
        </EDTooltip>
      );
    } else {
      return component;
    }
  }
);
EDLink.displayName = 'EDLink';

export default EDLink;
